import { useState } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";

import {
  DeleteOutlined,
  EllipsisOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import { Dropdown, Modal, Tooltip } from "antd";
import UserActivityStatus from "../../../../components/user-activity-status/user-activity-status";
import InfinityTable from "../../../../components/infinity-table/infinity-table";
import { Chips } from "../../../../components/chips/chips";
import AiDraftLabel from "../../../../components/table/ai-draft-label";

const DropdownActionItem = ({ remove, duplicate }) => {
  const items = [
    {
      key: "1",
      label: "Duplicate",
      icon: <CopyOutlined />,
      onClick: () => duplicate(),
    },
    {
      key: "2",
      label: "Delete",
      icon: <DeleteOutlined />,
      onClick: () => remove(),
    },
  ];

  return (
    <Dropdown
      menu={{
        items,
        onClick: (e) => {
          e.domEvent.stopPropagation();
        },
      }}
      trigger={["click"]}
      styles={{ root: { width: 120 } }}
    >
      <EllipsisOutlined
        style={{ cursor: "pointer", width: 30 }}
        onClick={(e) => {
          e.stopPropagation();
        }}
      />
    </Dropdown>
  );
};

const DeleteModalComponent = ({
  scenario,
  handleOk,
  handleCancel,
  isModalOpen,
}) => {
  return (
    <Modal
      title={scenario?.title}
      open={isModalOpen}
      onOk={handleOk}
      onCancel={handleCancel}
      okButtonProps={{ danger: true }}
      className="plan-page-action-modal"
    >
      <p>Are you sure you want to delete scenario?</p>
    </Modal>
  );
};

const ScenarioTable = ({
  updateSorting,
  data,
  onHandleRowClick,
  deleteScenario,
  isOnboarding,
  handleDuplicate,
  loading,
  hasMore,
  fetchData,
  selectionColumn,
  containerRef,
}) => {
  const [isModalOpen, setModalOpen] = useState();
  const [item, setItem] = useState();

  const { t } = useTranslation();
  const baseColumns = [
    {
      title: t("administration.scenario.table.name"),
      dataIndex: "title",
      render: (_, scenario) => <div><span>{scenario.title || "N/A"}</span>{!isOnboarding && <AiDraftLabel record={scenario} />}</div>,
      key: "title",
      width: isOnboarding ? "30%" : "25%",
      sorter: true,
    },
    isOnboarding
      ? {
          title: t("administration.scenario.table.cohort"),
          dataIndex: "cohort",
          render: (_, scenario) => {
            const cohortsText = (scenario.GroupOnboardingSurveys || [])
              .map((cohort) => cohort?.EnrollmentFieldOption?.value)
              .filter(Boolean)
              .join(", ");

            return cohortsText ? (
              <span>{cohortsText}</span>
            ) : (
              <Chips
                text={t("administration.scenario.table.cohort_not_assigned")}
                color="grey"
              />
            );
          },
          key: "cohort",
          width: "30%",
          sorter: true,
        }
      : {},
    {
      title: t("plan_management.table.status"),
      dataIndex: "status",
      render: (_, scenario) =>
        scenario.status ? (
          <UserActivityStatus style={{ whiteSpace: 'nowrap' }} userStatus={scenario.status} />
        ) : (
          <span>"N/A"</span>
        ),
      key: "status",
      width: isOnboarding ? "30%" : "12%",
      sorter: true,
    },
    {
      title: t("administration.scenario.table.last_modified"),
      dataIndex: "updatedAt",
      render: (_, scenario) => (
        <span style={{ whiteSpace: "nowrap" }}>
          {`${moment(scenario.updatedAt)
            .utc()
            .format("DD MMM YYYY, HH:mm")}, UTC` || "N/A"}
        </span>
      ),
      key: "updatedAt",
      width: isOnboarding ? "30%" : "20%",
      sorter: true,
    },
    isOnboarding
      ? {}
      : {
          title: t("administration.scenario.table.plan_association"),
          dataIndex: "planAssociations",
          render: (_, scenario) => {
            const plans = scenario.planAssociations || [];

            if (!plans.length) {
              return (
                <Chips
                  text={t("administration.scenario.table.no_plan_associations")}
                  color="grey"
                />
              );
            }

            const visiblePlans = plans.slice(0, 2);
            const restPlans = plans.slice(2);

            // Match the association chip look from the scenario 3-dots modal
            // (see associations-block.css .assoc-chip): translucent grey fill,
            // dark text. Sizes/paddings stay from lf-chips for consistency with
            // the rest of the table.
            const planChipStyle = {
              backgroundColor: "#6C6C8914",
              border: "none",
              color: "#111",
            };

            return (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {visiblePlans.map((plan) => (
                  <Chips key={plan.id} text={plan.title} style={planChipStyle} />
                ))}
                {restPlans.length > 0 && (
                  <Tooltip title={restPlans.map((plan) => plan.title).join(", ")}>
                    <span>
                      <Chips text={`+${restPlans.length}`} style={planChipStyle} />
                    </span>
                  </Tooltip>
                )}
              </div>
            );
          },
          key: "planAssociations",
          width: "33%",
        },
    {
      title: "Action",
      dataIndex: "action",
      render: (_, plan) => (
        <DropdownActionItem
          planId={plan.id}
          remove={() => handleOpenDeleteModal(plan)}
          duplicate={() => handleDuplicate(plan)}
        />
      ),
      key: "action",
      width: "10%",
    },
  ].filter((c) => c.title);

  const columns = selectionColumn ? [selectionColumn, ...baseColumns] : baseColumns;

  const handleOpenDeleteModal = (plan) => {
    setModalOpen(true);
    setItem(plan);
  };

  const handleCloseDeleteModal = () => {
    setModalOpen(false);
    setItem();
  };

  const handleOkPress = (id, name) => {
    handleCloseDeleteModal();
    deleteScenario(id, name);
  };

  const onChange = (_pagination, _filters, sorter) => {
    const sortField = sorter?.columnKey || sorter?.field || null;
    const sortOrder = sorter?.order === "ascend" ? "ASC"
                     : sorter?.order === "descend" ? "DESC"
                     : null;
    updateSorting(sortField, sortOrder);
  };

  return (
    <>
      <DeleteModalComponent
        scenario={item}
        handleOk={() => handleOkPress(item.id, item.title)}
        handleCancel={handleCloseDeleteModal}
        isModalOpen={isModalOpen}
      />
      <div ref={containerRef}>
        <InfinityTable
          data={data}
          tableColumns={columns}
          onHandleRowClick={onHandleRowClick}
          isLoading={loading}
          onChange={onChange}
          hasMore={hasMore}
          fetchData={fetchData}
        />
      </div>
    </>
  );
};

export default ScenarioTable;
